import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, Mail, Phone, FileText, CheckCircle, ChevronLeft, ChevronRight, ArrowRight, Scale, Shield } from 'lucide-react';
import { practiceAreas } from '../data';
import { useBookingStore } from '../store';

const timeSlots = [
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
  '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
];

function generateDates() {
  const dates = [];
  const today = new Date();
  for (let i = 1; i <= 14; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    if (d.getDay() !== 0 && d.getDay() !== 6) {
      dates.push(d);
    }
  }
  return dates;
}

export default function ConsultationPage() {
  const { step, setStep, selectedArea, setSelectedArea, selectedDate, setSelectedDate, selectedTime, setSelectedTime, formData, setFormData, resetBooking } = useBookingStore();
  const [submitted, setSubmitted] = useState(false);
  const dates = generateDates();

  const handleSubmit = () => {
    setSubmitted(true);
    setTimeout(() => {
      resetBooking();
      setTimeout(() => setSubmitted(false), 3000);
    }, 5000);
  };

  const steps = [
    { num: 1, label: 'Practice Area', icon: Scale },
    { num: 2, label: 'Date & Time', icon: Calendar },
    { num: 3, label: 'Your Details', icon: User },
    { num: 4, label: 'Confirmation', icon: CheckCircle },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
      {/* Hero */}
      <section className="pt-32 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050d1a] via-[#0a1628] to-[#0f2240]" />
        <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-[#c9a84c]/5 rounded-full blur-[150px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="gold-line mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Book a <span className="text-[#c9a84c]">Consultation</span>
          </h1>
          <p className="text-lg text-[#e8e4dc]/60 max-w-2xl">
            Schedule a confidential consultation with one of our experienced attorneys. We offer both in-office and virtual meetings.
          </p>
        </div>
      </section>

      {/* Security badges */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 text-xs text-[#e8e4dc]/50">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#0f2240]/50 border border-[#c9a84c]/10">
              <Shield className="w-3 h-3 text-[#c9a84c]" />
              <span>Attorney-Client Privilege Protected</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#0f2240]/50 border border-[#c9a84c]/10">
              <Shield className="w-3 h-3 text-[#c9a84c]" />
              <span>256-bit Encrypted</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#0f2240]/50 border border-[#c9a84c]/10">
              <CheckCircle className="w-3 h-3 text-[#c9a84c]" />
              <span>Confidential & Secure</span>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Steps indicator */}
          <div className="flex items-center justify-between mb-12">
            {steps.map((s, idx) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex items-center gap-2 ${step >= s.num ? 'text-[#c9a84c]' : 'text-[#e8e4dc]/30'} transition-colors`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    step >= s.num
                      ? 'bg-[#c9a84c]/20 border border-[#c9a84c]/40 text-[#c9a84c]'
                      : 'bg-[#0f2240]/50 border border-[#e8e4dc]/10'
                  }`}>
                    {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                  </div>
                  <span className="hidden md:block text-xs font-medium">{s.label}</span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-12 md:w-24 h-px mx-2 ${step > s.num ? 'bg-[#c9a84c]/40' : 'bg-[#e8e4dc]/10'} transition-colors`} />
                )}
              </div>
            ))}
          </div>

          {/* Step content */}
          <div className="min-h-[400px]">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16"
              >
                <div className="w-20 h-20 rounded-full bg-[#c9a84c]/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-[#c9a84c]" />
                </div>
                <h2 className="text-3xl font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Consultation <span className="text-[#c9a84c]">Confirmed!</span>
                </h2>
                <p className="text-[#e8e4dc]/60 mb-2">A confirmation email has been sent to {formData.email}</p>
                <p className="text-[#e8e4dc]/40 text-sm">You will receive a reminder 24 hours before your appointment.</p>
              </motion.div>
            ) : (
              <>
                {/* Step 1: Practice Area */}
                {step === 1 && (
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                    <h3 className="text-xl font-bold text-[#e8e4dc] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Select Practice Area</h3>
                    <p className="text-sm text-[#e8e4dc]/50 mb-6">Choose the area of law most relevant to your matter.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {practiceAreas.map((area) => (
                        <button
                          key={area.id}
                          onClick={() => { setSelectedArea(area.slug); setStep(2); }}
                          className={`p-5 rounded-xl text-left border transition-all duration-300 ${
                            selectedArea === area.slug
                              ? 'bg-[#c9a84c]/10 border-[#c9a84c]/40'
                              : 'bg-[#0f2240]/30 border-[#c9a84c]/10 hover:border-[#c9a84c]/20 hover:bg-[#0f2240]/50'
                          }`}
                        >
                          <span className="text-sm font-medium text-[#e8e4dc]">{area.title}</span>
                          <p className="text-xs text-[#e8e4dc]/40 mt-1 line-clamp-1">{area.description}</p>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Date & Time */}
                {step === 2 && (
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                    <h3 className="text-xl font-bold text-[#e8e4dc] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Select Date & Time</h3>
                    <p className="text-sm text-[#e8e4dc]/50 mb-6">Choose your preferred appointment date and time.</p>
                    
                    <div className="mb-8">
                      <p className="text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-4">Available Dates</p>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {dates.map((date) => {
                          const dateStr = date.toISOString().split('T')[0];
                          const isSelected = selectedDate === dateStr;
                          return (
                            <button
                              key={dateStr}
                              onClick={() => setSelectedDate(dateStr)}
                              className={`shrink-0 p-3 rounded-xl border text-center transition-all min-w-[70px] ${
                                isSelected
                                  ? 'bg-[#c9a84c]/15 border-[#c9a84c]/40'
                                  : 'bg-[#0f2240]/30 border-[#c9a84c]/10 hover:border-[#c9a84c]/20'
                              }`}
                            >
                              <div className="text-xs text-[#e8e4dc]/40">{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                              <div className={`text-lg font-bold ${isSelected ? 'text-[#c9a84c]' : 'text-[#e8e4dc]'}`}>
                                {date.getDate()}
                              </div>
                              <div className="text-xs text-[#e8e4dc]/40">{date.toLocaleDateString('en-US', { month: 'short' })}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {selectedDate && (
                      <div>
                        <p className="text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-4">Available Times</p>
                        <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                          {timeSlots.map((time) => (
                            <button
                              key={time}
                              onClick={() => setSelectedTime(time)}
                              className={`px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                                selectedTime === time
                                  ? 'bg-[#c9a84c]/15 border-[#c9a84c]/40 text-[#c9a84c]'
                                  : 'bg-[#0f2240]/30 border-[#c9a84c]/10 text-[#e8e4dc]/60 hover:border-[#c9a84c]/20'
                              }`}
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between mt-8">
                      <button onClick={() => setStep(1)} className="flex items-center gap-2 text-sm text-[#e8e4dc]/50 hover:text-[#c9a84c] transition-colors">
                        <ChevronLeft className="w-4 h-4" /> Back
                      </button>
                      <button
                        onClick={() => selectedDate && selectedTime && setStep(3)}
                        disabled={!selectedDate || !selectedTime}
                        className="flex items-center gap-2 px-6 py-2.5 bg-[#c9a84c]/20 border border-[#c9a84c]/40 text-[#c9a84c] rounded-xl text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#c9a84c]/30 transition-all"
                      >
                        Continue <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Personal Details */}
                {step === 3 && (
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                    <h3 className="text-xl font-bold text-[#e8e4dc] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Your Information</h3>
                    <p className="text-sm text-[#e8e4dc]/50 mb-6">Please provide your contact details for the consultation.</p>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">First Name</label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#e8e4dc]/30" />
                            <input
                              type="text"
                              value={formData.firstName}
                              onChange={(e) => setFormData({ firstName: e.target.value })}
                              className="w-full pl-10 pr-4 py-3 bg-[#0f2240]/40 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors placeholder:text-[#e8e4dc]/20"
                              placeholder="John"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">Last Name</label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#e8e4dc]/30" />
                            <input
                              type="text"
                              value={formData.lastName}
                              onChange={(e) => setFormData({ lastName: e.target.value })}
                              className="w-full pl-10 pr-4 py-3 bg-[#0f2240]/40 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors placeholder:text-[#e8e4dc]/20"
                              placeholder="Doe"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">Email Address</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#e8e4dc]/30" />
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ email: e.target.value })}
                            className="w-full pl-10 pr-4 py-3 bg-[#0f2240]/40 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors placeholder:text-[#e8e4dc]/20"
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">Phone Number</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#e8e4dc]/30" />
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ phone: e.target.value })}
                            className="w-full pl-10 pr-4 py-3 bg-[#0f2240]/40 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors placeholder:text-[#e8e4dc]/20"
                            placeholder="+1 (201) 555-0123"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">Brief Description of Your Matter</label>
                        <div className="relative">
                          <FileText className="absolute left-3 top-3 w-4 h-4 text-[#e8e4dc]/30" />
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ description: e.target.value })}
                            rows={4}
                            className="w-full pl-10 pr-4 py-3 bg-[#0f2240]/40 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors resize-none placeholder:text-[#e8e4dc]/20"
                            placeholder="Briefly describe your legal matter..."
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between mt-8">
                      <button onClick={() => setStep(2)} className="flex items-center gap-2 text-sm text-[#e8e4dc]/50 hover:text-[#c9a84c] transition-colors">
                        <ChevronLeft className="w-4 h-4" /> Back
                      </button>
                      <button
                        onClick={() => formData.firstName && formData.email && setStep(4)}
                        disabled={!formData.firstName || !formData.email}
                        className="flex items-center gap-2 px-6 py-2.5 bg-[#c9a84c]/20 border border-[#c9a84c]/40 text-[#c9a84c] rounded-xl text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#c9a84c]/30 transition-all"
                      >
                        Review Booking <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Step 4: Confirmation */}
                {step === 4 && (
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                    <h3 className="text-xl font-bold text-[#e8e4dc] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Review & Confirm</h3>
                    <p className="text-sm text-[#e8e4dc]/50 mb-6">Please review your booking details before confirming.</p>
                    <div className="p-6 rounded-2xl bg-[#0f2240]/40 border border-[#c9a84c]/15 space-y-4">
                      <div className="flex justify-between items-center pb-4 border-b border-[#c9a84c]/10">
                        <span className="text-sm text-[#e8e4dc]/50">Practice Area</span>
                        <span className="text-sm font-medium text-[#e8e4dc]">{practiceAreas.find(a => a.slug === selectedArea)?.title}</span>
                      </div>
                      <div className="flex justify-between items-center pb-4 border-b border-[#c9a84c]/10">
                        <span className="text-sm text-[#e8e4dc]/50">Date</span>
                        <span className="text-sm font-medium text-[#e8e4dc]">{new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="flex justify-between items-center pb-4 border-b border-[#c9a84c]/10">
                        <span className="text-sm text-[#e8e4dc]/50">Time</span>
                        <span className="text-sm font-medium text-[#e8e4dc]">{selectedTime}</span>
                      </div>
                      <div className="flex justify-between items-center pb-4 border-b border-[#c9a84c]/10">
                        <span className="text-sm text-[#e8e4dc]/50">Client</span>
                        <span className="text-sm font-medium text-[#e8e4dc]">{formData.firstName} {formData.lastName}</span>
                      </div>
                      <div className="flex justify-between items-center pb-4 border-b border-[#c9a84c]/10">
                        <span className="text-sm text-[#e8e4dc]/50">Email</span>
                        <span className="text-sm font-medium text-[#e8e4dc]">{formData.email}</span>
                      </div>
                      {formData.phone && (
                        <div className="flex justify-between items-center pb-4 border-b border-[#c9a84c]/10">
                          <span className="text-sm text-[#e8e4dc]/50">Phone</span>
                          <span className="text-sm font-medium text-[#e8e4dc]">{formData.phone}</span>
                        </div>
                      )}
                      {formData.description && (
                        <div className="pt-2">
                          <span className="text-sm text-[#e8e4dc]/50 block mb-2">Description</span>
                          <p className="text-sm text-[#e8e4dc]/70 bg-[#0a1628]/40 p-3 rounded-lg">{formData.description}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-between mt-8">
                      <button onClick={() => setStep(3)} className="flex items-center gap-2 text-sm text-[#e8e4dc]/50 hover:text-[#c9a84c] transition-colors">
                        <ChevronLeft className="w-4 h-4" /> Edit Details
                      </button>
                      <button
                        onClick={handleSubmit}
                        className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#c9a84c] to-[#b08e2e] text-[#0a1628] font-semibold rounded-xl hover:shadow-xl hover:shadow-[#c9a84c]/20 transition-all duration-300 hover:scale-105 text-sm"
                      >
                        Confirm Booking <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </motion.div>
  );
}
